import "@emotion/react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DiscoverTabView, DiscoverViewContext } from "contexts/DiscoverView";
import { SavedBrandContext } from "contexts/SavedBrand";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { useContext, useMemo, useState } from "react";

import { trackEvent } from "utils/tracking";

import Collections from "./Collections";
import MoveMenu from "./Collections/MoveMenu";
import NewCollectionDialog from "./Collections/NewCollectionDialog";
import ListHeader from "./ListHeader";
import ProgressBar from "./ProgressBar";
import FavoritesListMobile from "./mobile";
import styles from "./styles";

interface FavoritesListProps {
  closeDialog: () => void;
}

export default function FavoritesList({ closeDialog }: FavoritesListProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const {
    selectedCollectionId,
    setSelectedCollectionId,
    selectedCollection,
    getCollectionBrands,
    fetchCollections,
    currentTab,
    refreshSavedList,
    refreshLoading,
    collections: collectionsMap,
  } = useContext(SavedBrandCollectionsContext);
  const {
    setBulkBrands,
    setSelectAll,
    openSavedBrandsOnMobile,
    setOpenSavedBrandsOnMobile,
  } = useContext(SavedBrandContext);
  const { setDiscoverTab } = useContext(DiscoverViewContext);

  const [openCollectionDialog, setOpenCollectionDialog] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);

  const toggleBulkMode = () => {
    if (selectedCollectionId === null) return;
    trackEvent("Favorites List - Select Button Clicked", {
      "Collection ID": selectedCollectionId,
    });
    if (isBulkMode) {
      setBulkBrands([]);
      setSelectAll(false);
    }
    setIsBulkMode((prev) => !prev);
  };

  const handleBack = () => {
    if (selectedCollection) {
      setSelectedCollectionId(null);
    } else {
      setOpenSavedBrandsOnMobile(false);
    }
    setIsBulkMode(false);
    setBulkBrands([]);
    fetchCollections();
  };

  const editCollection = () => {
    if (selectedCollectionId === null) return;
    trackEvent("Favorites List - Edit Collection Button Clicked", {
      "Collection ID": selectedCollectionId,
    });
    setOpenCollectionDialog(true);
  };

  const handleCreateNewList = (bulkMove?: boolean) => {
    trackEvent("Favorites List - New List Button Clicked", {
      source: bulkMove ? "Move To Menu" : "New List Button",
    });
    setOpenCollectionDialog(true);
  };

  const displayedBrands =
    selectedCollectionId !== null
      ? getCollectionBrands(selectedCollectionId, currentTab)?.map(
          (x) => x.bentoBrand,
        )
      : [];

  const savedListCount = useMemo(
    () => Object.keys(collectionsMap || {}).length,
    [collectionsMap],
  );

  const collections = (
    <Collections
      isBulkMode={isBulkMode}
      displayedBrands={displayedBrands}
      onAddCollection={() => handleCreateNewList()}
    />
  );

  const header = (
    <ListHeader
      toggleBulkMode={toggleBulkMode}
      isBulkMode={isBulkMode}
      displayedBrands={displayedBrands}
    />
  );

  const footer = (
    <Grid
      container
      justifyContent={isBulkMode ? "flex-end" : "center"}
      sx={styles.footer}
    >
      {isBulkMode ? (
        <MoveMenu
          handleCreateNewList={handleCreateNewList}
          setIsBulkMode={setIsBulkMode}
          currentTab={currentTab}
        />
      ) : (
        <ProgressBar />
      )}
    </Grid>
  );

  return (
    <>
      {isMobileScreen ? (
        <>
          {openSavedBrandsOnMobile && (
            <FavoritesListMobile
              collections={collections}
              handleBack={handleBack}
              header={header}
              footer={footer}
            />
          )}
        </>
      ) : (
        <Box>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={styles.desktopHeader}
          >
            {selectedCollection ? (
              <>
                <Grid container item xs={9} alignItems="center">
                  <IconButton sx={styles.close} onClick={handleBack}>
                    <Box component="i" className="fa-regular fa-arrow-left" />
                  </IconButton>

                  <Typography sx={styles.collectionTitle}>
                    {selectedCollection.name}
                  </Typography>
                  <Box>
                    {!!selectedCollection.id &&
                    selectedCollection.id > 0 &&
                    !selectedCollection.isInstagramCollection ? (
                      <Tooltip title="Edit Collection">
                        <IconButton sx={styles.close} onClick={editCollection}>
                          <Box component="i" className="fa-regular fa-edit" />
                        </IconButton>
                      </Tooltip>
                    ) : null}

                    {!!selectedCollection.id &&
                      selectedCollection.isInstagramCollection && (
                        <Box
                          component="i"
                          className="fa-brands fa-instagram"
                          sx={{
                            color: "#E4405F",
                            ml: 0.5,
                            fontSize: "0.875rem",
                          }}
                        />
                      )}
                  </Box>
                </Grid>
                {!isMobileScreen && (
                  <Grid container item xs justifyContent="flex-end">
                    <IconButton sx={styles.close} onClick={closeDialog}>
                      <Box component="i" className="fa-regular fa-xmark" />
                    </IconButton>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item>
                  <Typography sx={styles.headerTitle}>Pitch Lists</Typography>
                  <Typography sx={styles.headerSubtitle}>
                    {savedListCount} saved list{savedListCount === 1 ? "" : "s"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Button
                    onClick={() =>
                      setDiscoverTab(DiscoverTabView.UPLOAD_BRANDS)
                    }
                    sx={styles.newListButton}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-plus"
                      sx={{ fontSize: 11, mr: 0.5 }}
                    />
                    New List
                  </Button>
                  <Tooltip title="Refresh">
                    <IconButton
                      sx={styles.headerIconButton}
                      onClick={refreshSavedList}
                    >
                      {refreshLoading ? (
                        <CircularProgress size={12} />
                      ) : (
                        <Box
                          component="i"
                          className="fa-regular fa-refresh"
                          sx={{ fontSize: 12 }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                  {!isMobileScreen && (
                    <IconButton
                      sx={styles.headerIconButton}
                      onClick={closeDialog}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-xmark"
                        sx={{ fontSize: 14 }}
                      />
                    </IconButton>
                  )}
                </Grid>
              </>
            )}
          </Grid>

          {header}
          {collections}
          {footer}
        </Box>
      )}

      <NewCollectionDialog
        open={openCollectionDialog}
        handleClose={() => setOpenCollectionDialog(false)}
        isBulkMode={isBulkMode}
        setIsBulkMode={setIsBulkMode}
      />
    </>
  );
}
